import {
  getRelativePosition,
  useGetArtworkById,
  useUpdateArtwork,
} from "@/api/artwork/artwork";
import {
  appendNoDuplicateRecordingNumbersRefine,
  artworkSchema,
} from "@/api/artwork/artwork-validation";
import { useGetAttachmentsByDocId } from "@/api/attachment";
import { useGetAllAuthors } from "@/api/author/author";
import { getCollectors, useGetCollector } from "@/api/collector/collector";
import ArtworkPrintSingleDrawer from "@/components/artwork/ArtworkPrintSingleDrawer";
import CreateArtworkSet from "@/components/ensemble/LinkSetsAndArtworks";
import { MaskableFormFieldWrapper } from "@/components/form-fields/MaskableFormFieldWrapper";
import * as AF from "@/components/form-items/AbstractForm";
import { CheckboxGroupMask } from "@/components/form-items/CheckboxGroupMask";
import { DateQualifierFields } from "@/components/form-items/DateQualifierFields";
import { DisplayModeCard } from "@/components/form-items/DisplayModeCard";
import { ImageFormData } from "@/components/images/MediaLibrary";
import { ArtworkSimplifiedView } from "./ArtworkSimplifiedView";
import {
  FourDimensionalInput,
  ThreeDimensionalInput,
} from "@/components/form-items/DimensionsInput";
import { useMultilingualEmpty } from "@/components/form-items/hooks/useMultilingualEmpty";
import { ModifiableList } from "@/components/form-items/ModifiableList";
import {
  DoubleMultilingualField,
  MultilingualField,
} from "@/components/form-items/MultilingualField";
import { MultilingualTitleAccordion } from "@/components/form-items/MultilingualTitleAccordion";
import { AbstractFormSelectFetcher } from "@/components/form-items/select-fetcher-form";
import LinksManagement from "@/components/LinksManagement";
import AddFileModal from "@/components/modal/AddFileModal";
import SculptureEditionsTable from "@/components/sculpture/SculptureEditionsTable";
import { Button } from "@/components/ui/button";
import { MaskableSeparator as Separator } from "@/components/form-items/MaskableSeparator";
import { useAuth } from "@/context/auth/AuthContext";
import { DashboardParamsProviders } from "@/context/DashboardParamsProviders";
import { FieldMaskingDisabledScope } from "@/context/FormFieldVisibilityContext";
import { toast } from "@/hooks/use-toast";
import { useDashboardParams } from "@/hooks/useDashboardParams";
import { getMainLanguage, isSupportedLocale, SupportedLocale } from "@/i18n";
import { FormLayout } from "@/layout/FormLayout";
import { useVisibleT } from "@/lib/cms/cms-new-i18n";
import {
  PHOTOGRAPHY_TYPE_VALUES,
  SCULPTURE_TYPE_VALUES,
} from "@/lib/compute-field/artwork-module-helpers";
import {
  computeFieldRelatedAuthorsArtwork,
  computeFieldSummaryArtwork,
  computeFieldSummaryPhotographieMasterArtwork,
} from "@/lib/computeFields";
import { appendFormattedDateToTitle } from "@/lib/date-qualifiers";
import { formatFormRegistrationReference } from "@/lib/form-registration-header";
import type { TypeDefaultImage } from "@/lib/image";
import { Artwork } from "@/lib/types";
import {
  generateImageName,
  resolveOeuvresDescriptionDisplayTitle,
} from "@/lib/utils";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import { useQueryClient } from "@tanstack/react-query";
import { Copy } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { FieldValues, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import DuplicateArtworkDialog from "@/components/artwork/DuplicateArtworkDialog";
import { MenubarItem } from "@/components/ui/menubar";
import { tWithKey } from "@/lib/i18n-fallback";
import { AddAdministrativeDocumentsDrawer } from "./AddAdministrativeDocument";
import { AdministrativeDocumentList } from "./AdministrativeDocumentList";
import { ArtworkFinancialMonitoringTab } from "./ArtworkFinancialMonitoringTab";
import { ArtworkHistoryTable } from "./ArtworkHistoryTable";
import TirageList from "./tirages/TirageList";

const schema = appendNoDuplicateRecordingNumbersRefine(
  artworkSchema.passthrough(),
);

const ARTWORK_PUBLISH_BLOCKING_FIELDS = [
  "oeuvresValidationFaux",
  "oeuvresValidationAttributionDouteuse",
  "oeuvresNotPublished",
  "oeuvresWaitingPublication",
] as const;

const EditArtworks = ({ artwork }: { artwork: Artwork }) => {
  const { instanceData } = useAuth();
  const mainLanguage = getMainLanguage();

  const defaultValues = useMemo(() => {
    const collectionId =
      artwork.oeuvresLocalisationHistoriqueCollectionId ??
      artwork.oeuvresLocalisationHistoriqueCollection?.id ??
      null;
    return {
      ...artwork,
      oeuvresDescriptionTitreMultilingualPublished:
        artwork.oeuvresDescriptionTitreMultilingualPublished ?? true,
      oeuvresValidationRemarquesPubliquesLabelPublished:
        artwork.oeuvresValidationRemarquesPubliquesLabelPublished ?? true,
      oeuvresLocalisationHistoriqueCollectionId: collectionId,
      // Case « fait partie du CR » = lien vers l'ensemble fixe (Réglages du CR).
      faitPartieDuCr: artwork.relatedEnsemble.some(
        (ens) => ens.ensemblesDescriptionType === "fixe",
      ),
      relatedAuthorIds: artwork.relatedAuthor.map((author) => author.id),
    };
  }, [artwork]);

  const form = useForm<FieldValues>({
    reValidateMode: "onSubmit",
    defaultValues,
    resolver: standardSchemaResolver(schema),
  });

  useEffect(() => {
    form.reset(defaultValues);
  }, [defaultValues, form]);

  const shouldDisplayVolume =
    form.watch("oeuvresdescriptiondimensionsprofondeur") || false;

  const isTirageKnownDescriptionEmpty = useMultilingualEmpty(
    "oeuvresTirageKnownDescription",
    form,
    "oeuvresTirageKnownDescriptionPublished",
  );

  const isTirageMasterDescriptionEmpty = useMultilingualEmpty(
    "oeuvresTirageMasterDescription",
    form,
    "oeuvresTirageMasterDescriptionPublished",
  );

  // TODO: apply MultiSelect search
  const { data: authors } = useGetAllAuthors({
    where: {},
    order: { artistesAuteursDescriptionNom: "asc" },
  });

  const { data: attachments = [] } = useGetAttachmentsByDocId(artwork.id);

  const { relativePosition } = useDashboardParams<Artwork>({
    statusField: "oeuvresPublicationStatus",
    resourceName: "artwork",
    relativePosition: {
      currentId: artwork.id,
      positionFetcher: getRelativePosition,
    },
  });

  const artworkUpdater = useUpdateArtwork();

  const queryClient = useQueryClient();

  const { t, i18n } = useTranslation();
  const vt = useVisibleT();
  const [temporarySetModalOpen, setTemporarySetModalOpen] = useState(false);
  const { user } = useAuth();
  const isReader = user?.role === "reader" ? true : false;
  const [refreshFiles, setRefreshFiles] = useState(false);
  const [duplicateOpen, setDuplicateOpen] = useState(false);
  const navigate = useNavigate();

  const sansTitreChecked = form.watch("oeuvresDescriptionTitreSansTitre");

  const publishBlockingValues = form.watch([
    ...ARTWORK_PUBLISH_BLOCKING_FIELDS,
  ]);
  const isPublishDisabled = publishBlockingValues.some(Boolean);

  useEffect(() => {
    if (sansTitreChecked) {
      const label = t("oeuvresDescriptionTitreSansTitre");
      form.setValue("oeuvresDescriptionTitre", label);
      const current =
        form.getValues("oeuvresDescriptionTitreMultilingual") ?? {};
      form.setValue("oeuvresDescriptionTitreMultilingual", {
        ...current,
        fr: label,
      });
    }
  }, [sansTitreChecked, form, t]);

  const collectionId = form.watch("oeuvresLocalisationHistoriqueCollectionId");
  const { data: selectedCollector } = useGetCollector(
    collectionId || undefined,
  );
  const prevCollectionIdRef = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    const collectorChanged = collectionId !== prevCollectionIdRef.current;
    if (prevCollectionIdRef.current === undefined) {
      prevCollectionIdRef.current = collectionId;
      return;
    }
    prevCollectionIdRef.current = collectionId;
    if (collectorChanged && collectionId && selectedCollector) {
      form.setValue(
        "oeuvresLocalisationHistoriqueCollectionCatalogue",
        selectedCollector.collectionneursDescriptionNomPublic ?? "",
      );
    }
  }, [collectionId, selectedCollector, form]);

  const handleResume = (data: any) => {
    // Map author ids to Author objects for the template
    const authorIds: string[] = data.relatedAuthorIds;
    const foundAuthors = authorIds
      .map((id) => authors?.find((author) => author.id === id))
      .filter((author) => author !== undefined);
    const dataWithAuthors = {
      ...data,
      relatedAuthor: foundAuthors,
    };
    if (isModulePhotoEnabled) {
      return computeFieldSummaryPhotographieMasterArtwork(dataWithAuthors, {
        language: getMainLanguage(),
        lineBreak: true,
      });
    }
    const summary = computeFieldSummaryArtwork(dataWithAuthors, {
      language: getMainLanguage(),
      lineBreak: true,
    });

    return summary;
  };

  /*
   * Start Here
   * Check if we need to display the "Tirages" tab
   */
  const oeuvresDescriptionTypeOeuvreMultilingual = form.watch(
    "oeuvresDescriptionTypeOeuvreMultilingual",
  );

  const artworkTypeInMainLanguage = isSupportedLocale(mainLanguage)
    ? oeuvresDescriptionTypeOeuvreMultilingual?.[mainLanguage]
    : undefined;

  const oeuvresDescriptionNumeroEnregistrement = form.watch(
    "oeuvresDescriptionNumeroEnregistrement",
  );
  const registrationLabel = t("backoffice.header.registrationNumber.label");
  const registrationReference = useMemo(
    () =>
      formatFormRegistrationReference(oeuvresDescriptionNumeroEnregistrement, {
        label: registrationLabel,
        locale: i18n.language,
        suffix: artworkTypeInMainLanguage,
      }),
    [
      oeuvresDescriptionNumeroEnregistrement,
      artworkTypeInMainLanguage,
      registrationLabel,
      i18n.language,
    ],
  );

  const isModulePhotoEnabled = useMemo(() => {
    if (!instanceData?.isModulePhotoEnabled) {
      return false;
    }

    if (!artworkTypeInMainLanguage) {
      return false;
    }

    return PHOTOGRAPHY_TYPE_VALUES.includes(artworkTypeInMainLanguage);
  }, [instanceData?.isModulePhotoEnabled, artworkTypeInMainLanguage]);

  const isModuleSculptureEnabled = useMemo(() => {
    if (!instanceData?.isModuleSculptureEnabled) {
      return false;
    }
    if (!artworkTypeInMainLanguage) {
      return false;
    }
    return SCULPTURE_TYPE_VALUES.includes(
      artworkTypeInMainLanguage as (typeof SCULPTURE_TYPE_VALUES)[number],
    );
  }, [instanceData?.isModuleSculptureEnabled, artworkTypeInMainLanguage]);

  const attachmentPositionType = useMemo<TypeDefaultImage>(
    () => (isModuleSculptureEnabled ? "sculptureEdition" : "artwork"),
    [isModuleSculptureEnabled],
  );

  const isSecureModuleReady = useMemo(() => {
    if (!instanceData) {
      return false;
    }

    const needsArtworkTypeForTabs =
      instanceData.isModulePhotoEnabled ||
      instanceData.isModuleSculptureEnabled;

    if (!needsArtworkTypeForTabs) {
      return true;
    }

    return artworkTypeInMainLanguage !== undefined;
  }, [instanceData, artworkTypeInMainLanguage]);
  /*
   * End Here
   */

  const provenanceMode = instanceData?.provenanceMode ?? false;

  const tabs = useMemo(
    () =>
      [
        {
          value: "generalInformation",
          label: t("artworksGeneralInfo"),
          nonHideable: true,
        },
        { value: "description", label: t("artworksDescription") },
        { value: "dimensions-poids", label: t("multipleDimensionsPoids") },
        {
          value: "socle",
          label: t("sculptureSocle"),
          hide: !isModuleSculptureEnabled,
        },
        {
          value: "localisation-historique",
          label: provenanceMode
            ? t("artwork.tabs.provenance")
            : t("artwork.tabs.historique"),
        },
        { value: "regie", label: t("artworksRegie") },
        {
          value: "rangement-recolement",
          label: t("artworksRangementRecolement"),
        },
        { value: "photo-copyright", label: t("artworksPhotosCrédits") },
        { value: "notes", label: t("notes") },
        { value: "sources", label: t("sources") },
        {
          value: "admin-docs",
          label: t("artworksValidationsDocumentsAdministratifs"),
        },
        { value: "files", label: t("fichiers") },
        { value: "suivi-financier", label: t("artworksSuiviFinancier") },
        { value: "links", label: t("linkedEntries") },
        {
          value: "tirages",
          label: t("tiragesTabLabel"),
          hide: !isModulePhotoEnabled,
          subtab: [
            {
              value: "general-description",
              label: t("tiragesGeneralInformation"),
            },
            {
              value: "tirages-list",
              label: t("tiragesListTabLabel"),
            },
          ],
        },
        {
          value: "sculpture",
          label: t("sculptureTabLabel"),
          hide: !isModuleSculptureEnabled,
          subtab: [
            {
              value: "sculpture-description-generale",
              label: t("sculptureDescriptionGenerale"),
            },
            {
              value: "sculpture-moule-matrice",
              label: t("sculptureMouleMatrice"),
            },
            {
              value: "sculpture-index-editions",
              label: t("sculptureIndexEditions"),
            },
          ],
        },
      ] as const,
    [t, isModulePhotoEnabled, isModuleSculptureEnabled, provenanceMode],
  );

  const [, setSearchParam] = useSearchParams();

  const titleFormatted = useMemo(() => {
    const title = resolveOeuvresDescriptionDisplayTitle(artwork, mainLanguage);
    return appendFormattedDateToTitle(
      title,
      artwork.oeuvresDescriptionDateDeCreation,
      {
        unknownDate: artwork.oeuvresDescriptionDateDeCreationInconnue,
        uncertainDate: artwork.oeuvresDescriptionDateDeCreationIncertaine,
        estimatedDate: artwork.oeuvresDescriptionDateDeCreationEstimee,
      },
    );
  }, [
    artwork,
    artwork.oeuvresDescriptionDateDeCreation,
    artwork.oeuvresDescriptionDateDeCreationInconnue,
    artwork.oeuvresDescriptionDateDeCreationIncertaine,
    artwork.oeuvresDescriptionDateDeCreationEstimee,
    mainLanguage,
  ]);

  const authorOptions = useMemo(
    () =>
      authors?.map((author) => ({
        label: `${author.artistesAuteursDescriptionPrenom} ${author.artistesAuteursDescriptionNom}`,
        value: author.id,
      })) ?? [],
    [authors],
  );

  // Labels of the visible Pro tabs (main + subtabs) — used by the simplified
  // view to size its left column exactly like the complete view's tab column.
  const tabLabels = useMemo(
    () =>
      (
        tabs as readonly {
          value: string;
          label?: string;
          hide?: boolean;
          subtab?: readonly {
            value: string;
            label?: string;
            hide?: boolean;
          }[];
        }[]
      )
        .filter((tab) => !tab.hide)
        .flatMap((tab) => [
          tab.label ?? tab.value,
          ...(tab.subtab ?? [])
            .filter((sub) => !sub.hide)
            .map((sub) => sub.label ?? sub.value),
        ]),
    [tabs],
  );

  const computeImageName = (data?: ImageFormData) =>
    generateImageName({
      primaryType: "artwork",
      artwork,
      translatedPosition: data?.position ? t(data?.position) : undefined,
    });

  const invalidateArtworkQueries = () => {
    queryClient.invalidateQueries({ queryKey: ["artworks"] });
    queryClient.invalidateQueries({
      queryKey: ["attachment", { docId: artwork.id }],
    });
    queryClient.invalidateQueries({ queryKey: ["artworksDashboard"] });
  };

  return (
    <>
    <FormLayout
      formKey="artwork"
      publishDisabled={isPublishDisabled}
      onSave={(status) => {
        if (status === "published" && isPublishDisabled) {
          return;
        }
        form.handleSubmit(
          (values: FieldValues) => {
            artworkUpdater.mutate(
              {
                ...(values as Artwork),
                ...(status && { oeuvresPublicationStatus: status }),
              },
              {
                onSuccess: () => {
                  toast({
                    title: t(
                      status === undefined
                        ? "autoSave"
                        : "recordSavedToastTitle",
                    ),
                    variant: "success",
                    description: t("artworkSavedToastDescription"),
                  });
                  form.reset({}, { keepValues: true });
                },
              },
            );
          },
          (invalid) => {
            if (invalid["oeuvresDescriptionNumeroEnregistrement"] != undefined)
              setSearchParam({ tab: "generalInformation" });
          },
        )();
      }}
      invalidateOnChange={invalidateArtworkQueries}
      simplifiedContent={
        <ArtworkSimplifiedView
          form={form}
          authorOptions={authorOptions}
          tabLabels={tabLabels}
          media={{
            id: artwork.id,
            type: attachmentPositionType,
            computeImageName,
            invalidateOnChange: invalidateArtworkQueries,
            embedAttachmentOnSubmit: true,
          }}
        />
      }
      title={titleFormatted}
      reference={registrationReference}
      subtitle={computeFieldRelatedAuthorsArtwork(artwork, {
        language: i18n.language as SupportedLocale,
      })}
      id={artwork.id}
      type="artwork"
      mediaLibraryPositionType={attachmentPositionType}
      publicationStatus={artwork.oeuvresPublicationStatus}
      picturesData={
        attachments && { pictures: attachments, type: attachmentPositionType }
      }
      linkOverview={`/edit/artwork/overview/${artwork.id}`}
      computeImageName={computeImageName}
      relativePosition={relativePosition}
      form={form}
      print={<ArtworkPrintSingleDrawer artwork={artwork} />}
      duplicate={
        isReader ? undefined : (
          <MenubarItem
            onClick={() => setDuplicateOpen(true)}
            className="gap-2"
          >
            <Copy className="h-4 w-4 shrink-0 text-[#64748B]" aria-hidden />
            {tWithKey(t, "duplicateArtwork", "Dupliquer la fiche d'œuvre")}
          </MenubarItem>
        )
      }
      embedAttachmentOnSubmit={true}
      keyStatus="oeuvresStatus"
    >
      <AF.AbstractForm
        form={form}
        formKey="artwork"
        tabs={tabs}
        ready={isSecureModuleReady}
        beforeTabs={<DisplayModeCard />}
      >
        <AF.Tab value="generalInformation">
          <AF.Col>
            <AF.Text
              name="oeuvresDescriptionNumeroEnregistrement"
              nonMaskable
              corner={() => (
                <AF.Switch
                  name="oeuvresDescriptionNumeroEnregistrementPublished"
                  labelFirst
                />
              )}
            />
            <AF.Text
              name="oeuvresDescriptionNumeroDInventaire"
              corner={() => (
                <AF.Switch
                  name="oeuvresDescriptionNumeroDInventairePublished"
                  labelFirst
                />
              )}
            />
            <AF.Text
              name="oeuvresDescriptionNumeroAutre"
              corner={() => (
                <AF.Switch
                  name="oeuvresInformationAutreNumeroPublished"
                  labelFirst
                />
              )}
            />
            <AF.Text
              name="oeuvresInformationAncienNumero"
              corner={() => (
                <AF.Switch
                  name="oeuvresInformationAncienNumeroPublished"
                  labelFirst
                />
              )}
            />
            <AF.Text name="oeuvresDescriptionReferenceCollectionActuelle" />
            <AF.Rating name="oeuvresValidationSystemeDeNotationDeLoeuvre" />
            <AF.Text name="oeuvresDescriptionTitre" nonMaskable />

            <AF.Checkbox
              name="faitPartieDuCr"
              label={vt(
                "bo.artwork.faitPartieDuCr",
                "Cette œuvre fait partie du CR",
              )}
            />
            <MaskableFormFieldWrapper
              fieldName="oeuvresValidationAjoutUnEnsembleTemporaire"
              label={t("oeuvresValidationAjoutUnEnsembleTemporaire")}
              className="w-full"
            >
              <Button
                onClick={() => setTemporarySetModalOpen(true)}
                type="button"
                variant="link"
                className="w-min p-0 text-sm font-medium font-['Raleway']"
              >
                {t("ensembleSelectEnsembleTemp")}
              </Button>

              <CreateArtworkSet
                disabled={isReader}
                open={temporarySetModalOpen}
                onOpenChange={setTemporarySetModalOpen}
                selectedArtworksIds={temporarySetModalOpen ? [artwork.id] : []}
                resetSelectedArtworks={() => setTemporarySetModalOpen(false)}
              />
            </MaskableFormFieldWrapper>
            <AF.Row>
              <AF.Checkbox
                name="oeuvresPhotosPhotoScanAFaire"
                visible={!isModulePhotoEnabled && !isModuleSculptureEnabled}
              />
              <AF.Checkbox
                name="oeuvresPhotosPhotoOkWeb"
                visible={!isModulePhotoEnabled && !isModuleSculptureEnabled}
              />
            </AF.Row>
            <AF.Checkbox
              name="oeuvresPhotosPhotoHdOk"
              visible={!isModulePhotoEnabled && !isModuleSculptureEnabled}
            />
            <AF.RichText
              name="oeuvresDescriptionResume"
              computeAction={handleResume}
            />
          </AF.Col>
          <AF.Col>
            <CheckboxGroupMask
              groupName="oeuvresValidationOkGroup"
              label={t("oeuvresValidationOkGroup")}
            >
              <AF.Row>
                <AF.Checkbox name="oeuvresValidationDescriptionOk" />
                <AF.Checkbox name="oeuvresValidationHistoriqueCollectionsOk" />
              </AF.Row>
              <AF.Row>
                <AF.Checkbox name="oeuvresValidationExpositionsOk" />
                <AF.Checkbox name="oeuvresValidationVentesOk" />
              </AF.Row>
            </CheckboxGroupMask>
            <Separator className="" />
            <CheckboxGroupMask
              groupName="oeuvresValidationEtatGroup"
              label={t("oeuvresValidationEtatGroup")}
            >
              <AF.Row>
                <AF.Checkbox name="oeuvresValidationOeuvreDocumentaire" />
                <AF.Checkbox name="oeuvresValidationOeuvreMajeure" />
              </AF.Row>
              <AF.Row>
                <AF.Checkbox name="oeuvresValidationOeuvreImportante" />
                <AF.Checkbox name="oeuvresValidationOeuvreInachevee" />
              </AF.Row>
              <Separator />
              <AF.Row>
                <AF.Checkbox name="oeuvresValidationFaux" />
                <AF.Checkbox name="oeuvresValidationCopie" />
              </AF.Row>
              <AF.Row>
                <AF.Checkbox name="oeuvresValidationDestroy" />
                <AF.Checkbox name="oeuvresValidationAttributionDouteuse" />
              </AF.Row>
              <AF.Row>
                <AF.Checkbox name="oeuvresValidationLost" />
                <AF.Checkbox name="oeuvresNotPublished" />
              </AF.Row>
              <AF.Row>
                <AF.Checkbox name="oeuvresValidationStole" />
                <AF.Checkbox name="oeuvresWaitingPublication" />
              </AF.Row>
            </CheckboxGroupMask>
            <Separator />
            <AF.ModifiableMultiSelectField
              listKey="oeuvresDescriptionAjoutDeMotsCles"
              name="oeuvresDescriptionAjoutDeMotsCles"
              maxCount={null}
              description={vt(
                "bo.fieldHelp.motsCles",
                "Les mots-clés sont utilisés pour la recherche sur le site public.",
              )}
            />
            <AF.Col>
              <Separator
                visible={isModulePhotoEnabled || isModuleSculptureEnabled}
              />
              <AF.Row>
                <AF.Checkbox
                  name="oeuvresPhotosPhotoScanAFaire"
                  visible={isModulePhotoEnabled || isModuleSculptureEnabled}
                />
                <AF.Checkbox
                  name="oeuvresPhotosPhotoOkWeb"
                  visible={isModulePhotoEnabled || isModuleSculptureEnabled}
                />
              </AF.Row>
              <AF.Checkbox
                name="oeuvresPhotosPhotoHdOk"
                visible={isModulePhotoEnabled || isModuleSculptureEnabled}
              />
              <Separator visible={isModulePhotoEnabled} />
            </AF.Col>
            <AF.RichText name="oeuvresValidationNotesDeTravail" />
          </AF.Col>
        </AF.Tab>
        <AF.Tab className="flex-col" value="description">
          <AF.Row className="items-start">
            <AF.Col>
              <AF.MultiSelectField
                name="relatedAuthorIds"
                options={
                  authors?.map((author) => ({
                    label: `${author.artistesAuteursDescriptionPrenom} ${author.artistesAuteursDescriptionNom}`,
                    value: author.id,
                  })) ?? []
                }
                label={t("estampesMultiplesDescriptionAuteurArtiste")}
                placeholder={t("select")}
              />
              <AF.RadioGroup
                groupName="oeuvresDescriptionNomArtisteOptionsGroup"
                label={t("oeuvresDescriptionAttributionOptionsGroup")}
                resettable={true}
                items={[
                  { value: "oeuvresDescriptionNomArtisteAttribueA" },
                  { value: "oeuvresDescriptionNomArtisteAnonyme" },
                  { value: "oeuvresDescriptionNomArtisteDApres" },
                  { value: "oeuvresDescriptionNomArtisteDansLeGoutDe" },
                  { value: "oeuvresDescriptionNomArtisteEntourageDe" },
                  { value: "oeuvresDescriptionNomArtisteAtelierDe" },
                ]}
              />
              <AF.Text name="oeuvresDescriptionTitre" nonMaskable />
              <MultilingualTitleAccordion
                name="oeuvresDescriptionTitreMultilingual"
                corner={() => (
                  <AF.Switch
                    name="oeuvresDescriptionTitreMultilingualPublished"
                    label={vt(
                      "artwork.form.title-multilingual.publish",
                      "Publier le titre multilingue",
                    )}
                    labelFirst
                  />
                )}
              />
              <AF.RadioGroup
                groupName="oeuvresDescriptionTitreOptionsGroup"
                label={t("oeuvresDescriptionTitreOptionsGroup")}
                resettable={true}
                items={[
                  { value: "oeuvresDescriptionTitreSansTitre" },
                  { value: "oeuvresDescriptionTitreInconnu" },
                  { value: "oeuvresValidationTitleNotConfirmed" },
                ]}
              />
              <AF.Text
                name="oeuvresDescriptionSousTitre"
                corner={() => (
                  <AF.Switch
                    name="oeuvresDescriptionSousTitrePublished"
                    label={t("artwork.form.sous-titre.publish")}
                    labelFirst
                  />
                )}
              />
              <AF.Text
                name="oeuvresDescriptionAutreTitre"
                corner={() => (
                  <AF.Switch
                    name="oeuvresDescriptionAutreTitrePublished"
                    label={t("artwork.form.autre-titre.publish")}
                    labelFirst
                  />
                )}
              />
              <AF.Text
                name="oeuvresDescriptionAncienTitre"
                label={t("oeuvresDescriptionAncienTitre")}
                corner={() => (
                  <AF.Switch
                    name="oeuvresDescriptionAncienTitrePublished"
                    label={t("artwork.form.ancien-titre.publish")}
                    labelFirst
                  />
                )}
              />

              <AF.Row>
                <AF.Checkbox
                  name="oeuvresDescriptionOeuvreDoubleFace"
                  align={true}
                  visible={!isModulePhotoEnabled}
                />
              </AF.Row>
              <AF.Row>
                <AF.Checkbox name="oeuvresDescriptionTechniqueConfirmer" />
                <AF.Checkbox name="oeuvresDescriptionSupportConfirmer" />
              </AF.Row>
              <AF.Col>
                <ModifiableList
                  name="oeuvresPhotoProjectDescription"
                  listKey="liste-projects-photo"
                  visible={isModulePhotoEnabled}
                  append
                  appendSeparator=" | "
                  corner={() => (
                    <AF.Switch
                      name="oeuvresPhotoProjectDescriptionPublished"
                      labelFirst
                    />
                  )}
                />
              </AF.Col>
              <ModifiableList
                name="oeuvresDescriptionLieuDeCreation"
                listKey="liste-lieux-creation"
                visible={isModulePhotoEnabled}
              />
            </AF.Col>
            <AF.Col>
              <DateQualifierFields
                names={{
                  date: "oeuvresDescriptionDateDeCreation",
                  unknown: "oeuvresDescriptionDateDeCreationInconnue",
                  uncertain: "oeuvresDescriptionDateDeCreationIncertaine",
                  estimated: "oeuvresDescriptionDateDeCreationEstimee",
                }}
              />
              <AF.Text
                name="oeuvresDescriptionReference"
                corner={() => (
                  <AF.Switch
                    name="oeuvresDescriptionReferencePublished"
                    label={t("artwork.form.reference.publish")}
                    labelFirst
                  />
                )}
              />
              <AF.ModifiableMultiSelectField
                name="oeuvresDescriptionCategorie"
                listKey="liste-categories"
                tooltip={vt(
                  "bo.artwork.fieldHelp.categorie",
                  "Catégorie(s) de classement de l'œuvre (ex. peinture, sculpture…).",
                )}
              />
              <AF.ModifiableMultiSelectField
                name="oeuvresDescriptionTheme"
                listKey="liste-themes"
                tooltip={vt(
                  "bo.artwork.fieldHelp.theme",
                  "Thème(s) associé(s) à l'œuvre.",
                )}
              />
              <AF.ModifiableMultiSelectField
                name="oeuvresDescriptionSerie"
                listKey="liste-series"
                tooltip={vt(
                  "bo.artwork.fieldHelp.serie",
                  "Série(s) à laquelle / auxquelles appartient l'œuvre.",
                )}
              />
              <ModifiableList
                name="oeuvresPhotoPeopleAndModelNames"
                listKey="liste-people-and-models"
                append
                appendSeparator=", "
              />
            </AF.Col>
          </AF.Row>
          <Separator />
          <AF.Col>
            <span>{t("forms.multilingual-fields.indication")}</span>
            <AF.Row className="items-start">
              <DoubleMultilingualField
                className="w-full"
                render={(lang) => (
                  <AF.Col>
                    <ModifiableList
                      name={`oeuvresDescriptionTypeOeuvreMultilingual.${lang}`}
                      label={t("oeuvresDescriptionTypeOeuvreMultilingual")}
                      listKey={`liste-types-oeuvres`}
                      lang={lang}
                    />
                    <ModifiableList
                      name={`oeuvresDescriptionTechnique.${lang}`}
                      label={t("oeuvresDescriptionTechnique")}
                      listKey={`liste-techniques`}
                      lang={lang}
                    />
                    <ModifiableList
                      label={t("oeuvresDescriptionSupport")}
                      name={`oeuvresDescriptionSupport.${lang}`}
                      listKey={`liste-supports`}
                      lang={lang}
                    />
                    <ModifiableList
                      label={t("oeuvresDescriptionRemarqueSupport")}
                      name={`oeuvresDescriptionRemarqueSupport.${lang}`}
                      listKey={`liste-remarques-support`}
                      lang={lang}
                    />
                    <ModifiableList
                      label={t("oeuvresDescriptionSignatureLocalisation")}
                      name={`oeuvresDescriptionSignatureLocalisation.${lang}`}
                      listKey={`liste-signatures`}
                      lang={lang}
                    />
                    <AF.Text
                      label={t("oeuvesDescriptionSignatureTranscription")}
                      name="oeuvresDescriptionSignatureTranscription"
                    />
                    <ModifiableList
                      label={t(
                        "artwork.form.oeuvresDescriptionInscriptionsDos",
                      )}
                      name={`oeuvresDescriptionInscriptionsDosMultilingual.${lang}`}
                      listKey="liste-inscriptions-dos"
                      lang={lang}
                      corner={() => (
                        <AF.Switch
                          name={`oeuvresDescriptionInscriptionsDosPublished.${lang}`}
                        />
                      )}
                    />
                  </AF.Col>
                )}
              />
            </AF.Row>
          </AF.Col>
          <Separator />
          <AF.Row className="items-start">
            <AF.Col>
              <ModifiableList
                name="oeuvresDescriptionRealisationFabrication"
                listKey="liste-fabricants-imprimeurs"
                visible={!isModulePhotoEnabled}
              />
              <AF.Row>
                <ModifiableList
                  name="oeuvresDescriptionPeriodeCreation"
                  listKey="liste-periodes-creation"
                />
                <ModifiableList
                  name="oeuvresDescriptionAireGeographique"
                  listKey="liste-aires-geographiques"
                />
              </AF.Row>
              <ModifiableList
                name="oeuvresDescriptionEcoleMouvement"
                listKey="liste-ecoles-mouvements"
              />
              <ModifiableList
                name="oeuvresDescriptionLieuDeCreation"
                listKey="liste-lieux-creation"
                visible={!isModulePhotoEnabled}
              />
            </AF.Col>
            <AF.Col>
              <AF.RichText name="oeuvresDescriptionDescriptionLongue" />
              <AF.Text name="oeuvresDescriptionNomFichierNumerique" />
              <AF.Checkbox name="oeuvresDescriptionRestaurationNumerique" />
            </AF.Col>
          </AF.Row>
        </AF.Tab>
        <AF.Tab value="dimensions-poids">
          <AF.Col>
            <FourDimensionalInput
              parentName="oeuvresDescriptionDimensionsOeuvre"
              needVolume={shouldDisplayVolume}
              needSurface={!shouldDisplayVolume}
            />
            <AF.RadioGroup
              groupName="oeuvresDescriptionDimensionsOeuvreOptionsGroup"
              label={t("oeuvresDescriptionDimensionsOptionsGroup")}
              items={[
                {
                  value: "oeuvresDescriptionDimensionsOeuvreVerifiees",
                },
                {
                  value: "oeuvresDescriptionDimensionsOeuvreInconnues",
                },
                {
                  value: "oeuvresDescriptionDimensionsOeuvreDouteuses",
                },
              ]}
            />
            <AF.Checkbox name="oeuvresdescriptiondimensionsprofondeur" />
            <AF.Col className="w-1/2">
              <AF.Text
                className="w-20"
                name="oeuvresDescriptionPoidsOeuvre"
                unit="kg"
                corner={() => (
                  <AF.Switch
                    name="oeuvresDescriptionPoidsOeuvrePublished"
                    labelFirst
                  />
                )}
              />
              <AF.Text
                name="oeuvresDescriptionDimensionsOeuvreRaw"
                label={t("oeuvresDescriptionDimensionsOeuvreRaw")}
                sublabel={t("oeuvresDescriptionDimensionsOeuvreRawSublabel")}
                expandOnOverflow
                rows={2}
                className="[field-sizing:content] min-h-[3.5rem] resize-none"
              />
            </AF.Col>
          </AF.Col>
        </AF.Tab>
        <AF.Tab value="localisation-historique">
          <AF.Col>
            <AbstractFormSelectFetcher
              label={t("oeuvresLocalisationHistoriqueCollection")}
              name="oeuvresLocalisationHistoriqueCollectionId"
              fetcher={async () => await getCollectors({})}
              queryKey={["collectors"]}
              allowClear={true}
              getContent={(collector) => (
                <>
                  {collector.collectionneursDescriptionNom}{" "}
                  {collector.collectionneursDescriptionPrenom}{" "}
                  {collector.collectionneursDescriptionVille
                    ? `(${collector.collectionneursDescriptionVille})`
                    : ""}
                </>
              )}
              getLabel={(collector) =>
                `${collector.collectionneursDescriptionNom} ${collector.collectionneursDescriptionPrenom} ${collector.collectionneursDescriptionVille ? `(${collector.collectionneursDescriptionVille})` : ""}`
              }
              getValue="id"
            />
            <AF.Checkbox name="oeuvresLocalisationHistoriqueOeuvreNonLocalisee" />
            <Separator />
            <ModifiableList
              name="oeuvresLocalisationHistoriqueCollectionCatalogue"
              listKey="liste-collections-catalogue"
              label={t("collectionneursDescriptionNomPublic")}
            />
            <Separator />
            <AF.RichText name="oeuvresLocalisationHistoriqueCollectionNote" />
            <Separator />
            <ArtworkHistoryTable
              artworkId={artwork.id}
              provenanceMode={provenanceMode}
            />
            <Separator />
            <AF.RichText name="oeuvresLocalisationHistoriqueNotes" />
            <Separator />
            <AF.Checkbox name="oeuvresLocalisationHistoriqueDepotAffectionPret" />
            <AF.Row>
              <AF.Text name="oeuvresLocalisationHistoriqueDateDepotAffectionPret" />
              <AF.Text name="oeuvresLocalisationHistoriqueDateRetourDepotAffectionPret" />
            </AF.Row>
            <AF.RichText name="oeuvresLocalisationHistoriqueNoteDepotAffectionPret" />
          </AF.Col>
        </AF.Tab>
        <AF.Tab value="regie">
          <AF.Col>
            <AF.Checkbox name="oeuvresRegieCadre" />
            <Separator />
            <AF.RichText name="oeuvresRegieDescriptionCadre" />
            <Separator />
            <ThreeDimensionalInput
              parentName="oeuvresRegieDimensionsCadre"
              volumeAutomaticToggle={true}
            />
            <Separator />
            <AF.RichText name="oeuvresRegieNotesRegie" />
          </AF.Col>
          <AF.Col>
            <AF.Checkbox name="oeuvresRegieCaisseTransport" />
            <Separator />
            <AF.RichText name="oeuvresRegieDescriptionCaisse" />
            <Separator />
            <ThreeDimensionalInput
              parentName="oeuvresRegieDimensionsCaisse"
              volumeAutomaticToggle={true}
            />
            <Separator />
            <AF.Row>
              <AF.Text
                name="oeuvresRegiePoidsCaisseAvecOeuvre"
                unit="kg"
                className="w-1/2"
              />
              <AF.Text
                name="oeuvresRegiePoidsCaisse"
                unit="kg"
                className="w-1/2"
              />
            </AF.Row>
            <Separator />
            <AF.RichText name="oeuvresRegieNotesCondition" />
            <AF.Checkbox name="oeuvresRestored" />
            <AF.Checkbox name="oeuvresToRestore" />
          </AF.Col>
        </AF.Tab>
        <AF.Tab value="rangement-recolement">
          <AF.Col>
            <ModifiableList
              name="oeuvresLocalisationHistoriqueLieuDeStockage"
              listKey="liste-lieux-stockage"
            />
            <AF.Row>
              <AF.Text name="oeuvresRangementRecolementPieceBox" />
              <AF.Text name="oeuvresRangementRecolementRangee" />
            </AF.Row>
            <AF.Row>
              <AF.Text name="oeuvresRangementRecolementRack" />
              <AF.Text name="oeuvresRangementRecolementMeuble" />
            </AF.Row>
            <AF.Row>
              <AF.Text name="oeuvresRangementRecolementEtagere" />
              <AF.Text name="oeuvresRangementRecolementBoiteCarton" />
            </AF.Row>
            <AF.Row>
              <AF.Text name="oeuvresRangementRecolementClasseur" />
              <AF.Text name="oeuvresRangementRecolementAutreReferenceRangement" />
            </AF.Row>
            <AF.Text
              name="oeuvresRangementRecolementDateRangement"
              className="w-[49%]"
            />
            <AF.RichText name="oeuvresLocalisationHistoriqueNotesRangement" />
          </AF.Col>
          <AF.Col>
            <AF.Checkbox name="oeuvresRangementRecolementMarquage" />
            <AF.Text
              name="oeuvresRangementRecolementDateMarquage"
              className="w-[49%]"
            />
            <Separator />
            <AF.Text
              name="oeuvresRangementRecolementOperateurRecolement"
              className="w-[49%]"
            />
            <AF.Row>
              <AF.Text name="oeuvresRangementRecolementDateRecolement" />
              <AF.Text name="oeuvresRangementRecolementReferenceCampagneRecolement" />
            </AF.Row>
            <AF.Row>
              <AF.Text name="oeuvresRangementRecolementAutreReferenceRecolement" />
              <AF.Checkbox
                name="oeuvresRangementRecolementRecolementOk"
                align={true}
              />
            </AF.Row>
            <AF.RichText name="oeuvresRangementRecolementNotesRecolement" />
          </AF.Col>
        </AF.Tab>
        <AF.Tab value="photo-copyright">
          <AF.Col>
            <AF.RichText name="oeuvresPhotosNotesPhotos" />
            <AF.RichText name="oeuvresPhotosNotesPreparationPhotos" />
            <ModifiableList
              name="oeuvresCreditsCreditOeuvre"
              listKey="liste-credits"
            />
            <ModifiableList
              name="oeuvresCreditsCreditPhotoOeuvre"
              listKey="liste-credits"
            />
            <AF.Row>
              <ModifiableList
                name="oeuvresCreditsNatureDroits"
                listKey="liste-credits"
              />
              <AF.LegacySelectCurrency name="oeuvresCreditsMontantDroits" />
            </AF.Row>
            <AF.RichText name="oeuvresCreditsRemarqueCreditsDroits" />
          </AF.Col>
        </AF.Tab>
        <AF.Tab value="notes">
          <AF.Col>
            <AF.RichText name="oeuvresValidationNotesDeTravail" />
            <AF.Switch
              name="oeuvresValidationRemarquesPubliquesLabelPublished"
              labelFirst
              maskable
            />
            <div className="flex w-full gap-4">
              <DoubleMultilingualField
                name="oeuvresValidationRemarquesPubliquesMultilingual"
                render={(lang) => (
                  <AF.RichText
                    name={`oeuvresValidationRemarquesPubliquesMultilingual.${lang}`}
                    label={t(
                      "artwork.form.oeuvresValidationRemarquesPubliques",
                    )}
                  />
                )}
              />
            </div>
            <AF.RichText name="oeuvresValidationRemarquesPersonnelles" />
          </AF.Col>
        </AF.Tab>
        <AF.Tab value="sources">
          <AF.Col>
            <AF.RichText name="sources" />
          </AF.Col>
        </AF.Tab>
        <AF.Tab
          value="admin-docs"
          headerExtras={
            /* Masquage désactivé sur le drawer de création : ses champs de
               saisie doivent rester visibles même après un « Tout masquer »,
               sinon plus rien ne permettrait de les réafficher. */
            <FieldMaskingDisabledScope>
              <AddAdministrativeDocumentsDrawer
                disabled={isReader}
                artwork={artwork}
                artworkAttachments={attachments}
              />
            </FieldMaskingDisabledScope>
          }
        >
          <AF.Col>
            {/* Idem pour le drawer de renommage porté par la liste. */}
            <FieldMaskingDisabledScope>
              <AdministrativeDocumentList artworkId={artwork.id} />
            </FieldMaskingDisabledScope>
            <Separator />
            <AF.Checkbox name="oeuvresAdminDocLettreInclusionEdit" />
            <Separator />
            <AF.Checkbox name="oeuvresAdminDocCertificatExist" />
            <Separator />
            <AF.RichText name="oeuvresAdministratifNotes" />
          </AF.Col>
        </AF.Tab>
        <AF.Tab
          isForm={false}
          value="files"
          headerExtras={
            <AddFileModal
              id={artwork.id}
              onClose={() => {
                setRefreshFiles(true);
              }}
            />
          }
        >
          <AF.OthersFilesTable
            docId={artwork.id}
            name="diversfiles"
            refreshFiles={refreshFiles}
            setRefreshFiles={setRefreshFiles}
          />
        </AF.Tab>
        {/* Masquage de champs désactivé sur tout l'onglet : ses formulaires
            (montants, dates, popups d'ajout) doivent rester saisissables même
            après un "Tout masquer". */}
        <AF.Tab value="suivi-financier" isForm={false} disableFieldMasking>
          <ArtworkFinancialMonitoringTab relatedArtworkId={artwork.id} />
        </AF.Tab>
        <AF.Tab value="links">
          <LinksManagement
            id={artwork.id}
            type="artwork"
            data={artwork}
            configuration={{
              fields: [
                {
                  name: "links",
                  type: "links",
                  label: "Liens",
                  links: [
                    {
                      name: "relatedArtwork",
                      label: t("entryartworks"),
                      type: "link-simple",
                    },
                    {
                      name: "relatedMultipleprint",
                      label: t("entrymultipleprints"),
                      type: "link-simple",
                    },
                    {
                      name: "externalLinks",
                      label: t("entryExternalLinks"),
                      type: "link-simple",
                    },
                    {
                      name: "artworkInExhibitions",
                      label: t("entryexhibitions"),
                      type: "link-complex",
                      fields: [
                        {
                          name: "exhibition.expositionsDescriptionNumeroExpo",
                          label: t("exhibitionDescriptionNumeroExpo"),
                          type: "text",
                          className: "w-full",
                          disabled: true,
                        },
                        {
                          name: "exhibition.expositionsDescriptionResume",
                          label: t("exhibitionDescriptionResume"),
                          type: "richtext",
                          className: "w-full",
                          disabled: true,
                        },
                        {
                          name: "oeuvresDansExpositionsDescriptionNumeroCatalogue",
                          label: t(
                            "oeuvresDansExpositionsDescriptionNumeroCatalogue",
                          ),
                          type: "text",
                          className: "w-full",
                        },
                        {
                          name: "oeuvresDansExpositionsNotes",
                          label: t("oeuvresDansExpositionsNotes"),
                          type: "richtext",
                          className: "w-full",
                        },
                      ],
                    },
                    {
                      name: "artworkInBibliography",
                      label: t("entrybibliographies"),
                      type: "link-complex",
                      fields: [
                        // Ligne 1 : Numéro biblio
                        {
                          type: "row",
                          className: "items-start w-full",
                          children: [
                            {
                              name: "bibliography.bibliographieDescriptionNumeroBibliographie",
                              label: t(
                                "bibliographieDescriptionNumeroBibliographie",
                              ),
                              type: "text",
                              className: "w-full",
                              disabled: true,
                            },
                          ],
                        },
                        // Ligne 2 : Résumé biblio
                        {
                          type: "row",
                          className: "items-start w-full",
                          children: [
                            {
                              name: "bibliography.bibliographieDescriptionResumeLong",
                              label: t("bibliographieDescriptionResumeLong"),
                              type: "richtext",
                              className: "w-full",
                              disabled: true,
                            },
                          ],
                        },
                        // Ligne 3 : Repr P. - Repr planche numéro
                        {
                          type: "row",
                          className: "grid grid-cols-2 w-full gap-4",
                          children: [
                            {
                              name: "oeuvresDansBibliographieDescriptionReproduitPage",
                              label: t(
                                "oeuvresDansBibliographieDescriptionReproduitPage",
                              ),
                              type: "text",
                              className: "w-full",
                            },
                            {
                              name: "oeuvresDansBibliographieDescriptionReproduitPlanche",
                              label: t(
                                "oeuvresDansBibliographieDescriptionReproduitPlanche",
                              ),
                              type: "text",
                              className: "w-full",
                            },
                          ],
                        },
                        // Ligne 4 : Planche non numérotée
                        {
                          type: "row",
                          className: "grid grid-cols-2 w-full -mt-3 pt-0",
                          children: [
                            {
                              name: "oeuvresDansBibliographieDescriptionPlancheNonNumerotee",
                              label: t(
                                "oeuvresDansBibliographieDescriptionPlancheNonNumerotee",
                              ),
                              type: "checkbox",
                              className: "w-full col-start-2",
                            },
                          ],
                        },
                        // Ligne 5 : Cité page
                        {
                          type: "row",
                          className: "grid grid-cols-2 w-full gap-4",
                          children: [
                            {
                              name: "oeuvresDansBibliographieDescriptionCitePage",
                              label: t(
                                "oeuvresDansBibliographieDescriptionCitePage",
                              ),
                              type: "text",
                              className: "w-full",
                            },
                          ],
                        },
                        // Ligne 6 : Listé page - Sous le numéro
                        {
                          type: "row",
                          className: "grid grid-cols-2 w-full gap-4",
                          children: [
                            {
                              name: "oeuvresDansBibliographieDescriptionListePage",
                              label: t(
                                "oeuvresDansBibliographieDescriptionListePage",
                              ),
                              type: "text",
                              className: "w-full",
                            },
                            {
                              name: "oeuvresDansBibliographieDescriptionSousNumero",
                              label: t(
                                "oeuvresDansBibliographieDescriptionSousNumero",
                              ),
                              type: "text",
                              className: "w-full",
                            },
                          ],
                        },
                        // Ligne 7 : Photographe
                        {
                          type: "row",
                          className: "items-start w-full",
                          children: [
                            {
                              name: "oeuvresDansBibliographieDescriptionPhotographe",
                              label: t(
                                "oeuvresDansBibliographieDescriptionPhotographe",
                              ),
                              type: "text",
                              className: "w-full",
                            },
                          ],
                        },
                        // Ligne 8 : Non paginé - repr en couverture
                        {
                          type: "row",
                          className: "grid grid-cols-2 w-full gap-4",
                          children: [
                            {
                              name: "oeuvresDansBibliographieDescriptionNonPagine",
                              label: t(
                                "oeuvresDansBibliographieDescriptionNonPagine",
                              ),
                              type: "checkbox",
                              className: "w-full",
                            },
                            {
                              name: "oeuvresDansBibliographieDescriptionReproductionCouverture",
                              label: t(
                                "oeuvresDansBibliographieDescriptionReproductionCouverture",
                              ),
                              type: "checkbox",
                              className: "w-full",
                            },
                          ],
                        },
                        // Ligne 9 : Repr couleur
                        {
                          type: "row",
                          className: "items-start w-full",
                          children: [
                            {
                              name: "oeuvresDansBibliographieDescriptionReproductionCouleur",
                              label: t(
                                "oeuvresDansBibliographieDescriptionReproductionCouleur",
                              ),
                              type: "checkbox",
                              className: "w-full",
                            },
                          ],
                        },
                        // Ligne 10 : Texte libre
                        {
                          type: "row",
                          className: "items-start w-full",
                          children: [
                            {
                              name: "oeuvresDansBibliographieDescriptionTexteLibre",
                              label: t(
                                "oeuvresDansBibliographieDescriptionTexteLibre",
                              ),
                              type: "text",
                              className: "w-full",
                            },
                          ],
                        },
                        // Ligne 11 : Notes
                        {
                          type: "row",
                          className: "items-start w-full",
                          children: [
                            {
                              name: "oeuvresDansBibliographieNotes",
                              label: t("oeuvresDansBibliographieNotes"),
                              type: "richtext",
                              className: "w-full",
                            },
                          ],
                        },
                      ],
                    },
                    {
                      name: "artworkInAuctions",
                      label: t("entryauctions"),
                      type: "link-complex",
                      fields: [
                        {
                          name: "auction.ventesDescriptionNumeroVente",
                          label: t("auctionDescriptionNumeroVente"),
                          type: "text",
                          className: "w-full",
                          disabled: true,
                        },
                        {
                          name: "auction.ventesDescriptionResume",
                          label: t("auctionDescriptionResume"),
                          type: "richtext",
                          className: "w-full",
                          disabled: true,
                        },
                        {
                          name: "oeuvresDansVentesDescriptionNumeroLot",
                          label: t("oeuvresDansVentesDescriptionNumeroLot"),
                          type: "text",
                          className: "w-full",
                        },
                        {
                          name: "oeuvresDansVentesDescriptionReproduitPage",
                          label: t("oeuvresDansVentesDescriptionReproduitPage"),
                          type: "text",
                          className: "w-full",
                        },
                        {
                          name: "oeuvresDansVentesDescriptionEstimationBasse",
                          label: t(
                            "oeuvresDansVentesDescriptionEstimationBasse",
                          ),
                          type: "inputSelectCurrency",
                          className: "w-full",
                          defaultSelectValue: "EUR",
                        },
                        {
                          name: "oeuvresDansVentesDescriptionEstimationHaute",
                          label: t(
                            "oeuvresDansVentesDescriptionEstimationHaute",
                          ),
                          type: "inputSelectCurrency",
                          className: "w-full",
                          defaultSelectValue: "EUR",
                        },
                        {
                          name: "oeuvresDansVentesDescriptionVendu",
                          label: t("oeuvresDansVentesDescriptionVendu"),
                          type: "checkbox",
                          className: "w-full",
                        },
                        {
                          name: "oeuvresDansVentesDescriptionResultatNonListe",
                          label: t(
                            "oeuvresDansVentesDescriptionResultatNonListe",
                          ),
                          type: "checkbox",
                          className: "w-full",
                        },
                        {
                          name: "oeuvresDansVentesDescriptionNonVenu",
                          label: t("oeuvresDansVentesDescriptionNonVenu"),
                          type: "checkbox",
                          className: "w-full",
                        },
                        {
                          name: "oeuvresDansVentesDescriptionNonVendu",
                          label: t("oeuvresDansVentesDescriptionNonVendu"),
                          type: "checkbox",
                          className: "w-full",
                        },
                        {
                          name: "oeuvresDansVentesDescriptionNonReproduit",
                          label: t("oeuvresDansVentesDescriptionNonReproduit"),
                          type: "checkbox",
                          className: "w-full",
                        },
                        {
                          name: "oeuvresDansVentesDescriptionRetire",
                          label: t("oeuvresDansVentesDescriptionRetire"),
                          type: "checkbox",
                          className: "w-full",
                        },
                        {
                          name: "oeuvresDansVentesDescriptionMiseAPrix",
                          label: t("oeuvresDansVentesDescriptionMiseAPrix"),
                          type: "inputSelectCurrency",
                          defaultSelectValue: "EUR",
                          className: "w-full",
                        },
                        {
                          name: "oeuvresDansVentesDescriptionPrixMarteau",
                          label: t("oeuvresDansVentesDescriptionPrixMarteau"),
                          type: "inputSelectCurrency",
                          defaultSelectValue: "EUR",
                          className: "w-full",
                        },
                        {
                          name: "oeuvresDansVentesDescriptionPrixAvecFrais",
                          label: t("oeuvresDansVentesDescriptionPrixAvecFrais"),
                          type: "inputSelectCurrency",
                          defaultSelectValue: "EUR",
                          className: "w-full",
                        },
                        {
                          name: "oeuvresDansVentesDescriptionNotes",
                          label: t("oeuvresDansVentesDescriptionNotes"),
                          type: "richtext",
                          className: "w-full",
                        },
                      ],
                    },
                    {
                      name: "relatedSculptureEditions",
                      label: t("entrysculptureeditions"),
                      type: "link-simple",
                      hidden: !isModuleSculptureEnabled,
                    },
                    {
                      name: "relatedAudiovisual",
                      label: t("entryaudiovisuals"),
                      type: "link-simple",
                    },
                    {
                      name: "relatedDocument",
                      label: t("entrydocuments"),
                      type: "link-simple",
                    },
                    {
                      name: "relatedWriting",
                      label: t("entrywritings"),
                      type: "link-simple",
                    },
                    {
                      name: "relatedPhotography",
                      label: t("entryphotographies"),
                      type: "link-simple",
                    },
                  ],
                },
              ],
            }}
          />
        </AF.Tab>
        <AF.Tab value="tirages-general-description">
          <AF.Col>
            <MultilingualField
              name="oeuvresTirageKnownDescription"
              corner={(_) => (
                <AF.Switch
                  name={`oeuvresTirageKnownDescriptionPublished`}
                  switchProps={{ disabled: isTirageKnownDescriptionEmpty }}
                />
              )}
              render={(lang) => (
                <AF.RichText
                  name={`oeuvresTirageKnownDescription.${lang}`}
                  label={t("artwork.form.oeuvresTirageKnownDescription")}
                />
              )}
            />
            <MultilingualField
              name="oeuvresTirageMasterDescription"
              corner={(_) => (
                <AF.Switch
                  name={`oeuvresTirageMasterDescriptionPublished`}
                  switchProps={{ disabled: isTirageMasterDescriptionEmpty }}
                />
              )}
              render={(lang) => (
                <AF.RichText
                  name={`oeuvresTirageMasterDescription.${lang}`}
                  label={t("artwork.form.oeuvresTirageMasterDescription")}
                />
              )}
            />
          </AF.Col>
        </AF.Tab>
        <AF.Tab value="tirages-tirages-list" showHeader={false}>
          <DashboardParamsProviders
            storageKey="tirage"
            persistInSearchParams={false}
          >
            <TirageList artworkId={artwork.id} />
          </DashboardParamsProviders>
        </AF.Tab>
        <AF.Tab value="socle">
          <AF.Col>
            <MultilingualField
              name="oeuvresSocleDescription"
              render={(lang) => (
                <AF.RichText
                  name={`oeuvresSocleDescription.${lang}`}
                  label={t("artwork.form.oeuvresSocleDescription")}
                />
              )}
              corner={(_) => (
                <AF.Switch
                  name="oeuvresSocleDescriptionPublished"
                  label={t("artwork.form.socle.description.publish")}
                  labelFirst
                />
              )}
            />
            <FourDimensionalInput
              parentName="oeuvresSocleDimensions"
              needVolume={true}
              needSurface={false}
            />
            <AF.Text className="w-20" name="oeuvresSoclePoids" unit="kg" />
          </AF.Col>
        </AF.Tab>
        <AF.Tab value="sculpture-sculpture-description-generale">
          <AF.Col>
            <MultilingualField
              name="oeuvresSculptureDescriptionEditions"
              className="w-full"
              render={(locale) => (
                <AF.RichText
                  name={`oeuvresSculptureDescriptionEditions.${locale}`}
                  label={t("artwork.form.sculpture.description-editions")}
                />
              )}
              corner={() => (
                <AF.Switch
                  name="oeuvresSculptureDescriptionEditionsPublished"
                  label={t(
                    "artwork.form.sculpture.description-editions.publish",
                  )}
                  labelFirst
                />
              )}
            />
            <MultilingualField
              name="oeuvresSculpturePresentationSculpture"
              className="w-full"
              render={(locale) => (
                <AF.RichText
                  name={`oeuvresSculpturePresentationSculpture.${locale}`}
                  label={t("artwork.form.sculpture.presentation-sculpture")}
                />
              )}
              corner={() => (
                <AF.Switch
                  name="oeuvresSculpturePresentationSculpturePublished"
                  label={t(
                    "artwork.form.sculpture.presentation-sculpture.publish",
                  )}
                  labelFirst
                />
              )}
            />
            <MultilingualField
              name="oeuvresSculptureNotesGenerales"
              className="w-full"
              render={(locale) => (
                <AF.RichText
                  name={`oeuvresSculptureNotesGenerales.${locale}`}
                  label={t("artwork.form.sculpture.notes-generales")}
                />
              )}
              corner={() => (
                <AF.Switch
                  name="oeuvresSculptureNotesGeneralesPublished"
                  label={t("artwork.form.sculpture.notes-generales.publish")}
                  labelFirst
                />
              )}
            />
          </AF.Col>
        </AF.Tab>
        <AF.Tab value="sculpture-sculpture-moule-matrice">
          <AF.Col>
            <MultilingualField
              name="oeuvresSculptureMouleDescription"
              className="w-full"
              render={(locale) => (
                <AF.RichText
                  name={`oeuvresSculptureMouleDescription.${locale}`}
                  label={t("artwork.form.sculpture.moule.description")}
                />
              )}
            />
            <AF.Text
              name="oeuvresSculptureMouleNombreElements"
              label={t("artwork.form.sculpture.moule.nombre-elements")}
            />
            <FourDimensionalInput
              parentName="oeuvresSculptureMouleDimensions"
              needVolume={true}
              needSurface={false}
            />
            <AF.Text
              name="oeuvresSculptureMouleDimensionsPersonnalisees"
              label={t(
                "artwork.form.sculpture.moule.dimensions-personnalisees",
              )}
            />
            <AF.Text
              name="oeuvresSculptureMoulePoids"
              unit="kg"
              label={t("artwork.form.sculpture.moule.poids")}
              className="w-20"
            />
            <AF.Row className="items-center gap-4 [&>*]:min-w-0 [&>*]:flex-1">
              <ModifiableList
                name="oeuvresSculptureMouleLocalisation"
                listKey="liste-localisation-moule"
              />
              <AF.Row>
                <AF.Checkbox
                  name="oeuvresSculptureMouleNonLocalise"
                  label={t("artwork.form.sculpture.moule.non-localise")}
                  className="mt-6"
                />
                <AF.Checkbox
                  name="oeuvresSculptureMouleDestroy"
                  label={t("artwork.form.sculpture.moule.destroy")}
                  className="mt-6"
                />
              </AF.Row>
            </AF.Row>
            <ModifiableList
              name="oeuvresSculptureMouleFabricant"
              listKey="liste-fabricant-moule"
            />
            <AF.LegacySelectCurrency
              name="oeuvresSculptureMouleCoutRevient"
              label={t("artwork.form.sculpture.moule.cout-revient")}
            />
            <AF.RichText
              name="oeuvresSculptureMouleNotes"
              label={t("artwork.form.sculpture.moule.notes")}
            />
          </AF.Col>
        </AF.Tab>
        <AF.Tab value="sculpture-sculpture-index-editions" showHeader={false}>
          <DashboardParamsProviders
            storageKey="sculpture-edition"
            persistInSearchParams={false}
          >
            <SculptureEditionsTable
              masterArtworkId={artwork.id}
              artworkTitle={artwork.oeuvresDescriptionTitre ?? ""}
            />
          </DashboardParamsProviders>
        </AF.Tab>
      </AF.AbstractForm>
    </FormLayout>
    <DuplicateArtworkDialog
      open={duplicateOpen}
      onOpenChange={setDuplicateOpen}
      sourceArtworkId={artwork.id}
      sourceNumeroEnregistrement={artwork.oeuvresDescriptionNumeroEnregistrement}
      onDuplicateSuccess={(created) => {
        setDuplicateOpen(false);
        navigate(`/edit/artwork/${created.id}`);
      }}
    />
    </>
  );
};

export const EditArtworksPage = () => {
  const { id } = useParams();
  const { t } = useTranslation();

  const { data, isLoading } = useGetArtworkById(id, false);

  return (
    <>
      {data && <EditArtworks key={data.id} artwork={data} />}
      {!data && !isLoading && (
        <h2 className="p-4">{t("artworkNexistePasLabel")}</h2>
      )}
    </>
  );
};
